import React, { useRef } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import AsyncSelect from '../inputs/AsyncSelect';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * An async select form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentProps<AsyncSelect>} AsyncSelectProps
 *
 * @param {BaseFieldRenderProps & AsyncSelectProps} fieldRenderProps
 * @returns {React.ReactElement} Async select form field.
 */
const FormAsyncSelect = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    name,
    id,
    valid,
    disabled,
    hint,
    wrapperStyle,
    clearButton,
    wrapperClassName,
    optional,
    value,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  if (!label) {
    return (
      <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
        <AsyncSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          clearButton={disabled ? false : clearButton}
          value={value}
          dataTestId={editorId}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorRef={editorRef}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value}
      >
        <AsyncSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          clearButton={disabled ? false : clearButton}
          value={value}
          dataTestId={editorId}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormAsyncSelect;
